import * as React from 'react';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import Radio from './radio';
import { ConfigContext } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import { RadioGroupContextProvider } from './context';
import getDataOrAriaProps from '../_util/getDataOrAriaProps';
const RadioGroup = React.forwardRef((props, ref) => {
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const size = React.useContext(SizeContext);
    const groupRef = React.useRef(null);
    // 滑块宽度
    const [sliderWidth, setSliderWidth] = React.useState(0);
    // 滑块偏移量
    const [sliderOffset, setSliderOffset] = React.useState(0);
    const [value, setValue] = useMergedState(props.defaultValue, {
        value: props.value,
    });
    React.useEffect(() => {
        if (props.buttonStyle === 'diffusion' &&
            !isNil(value) &&
            groupRef.current &&
            !isEmpty(props.children)) {
            requestAnimationFrame(() => {
                var _a;
                // 获取选中的radio按钮元素
                const prefixCls = getPrefixCls('radio', props.prefixCls);
                const checkedRadio = (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`.${prefixCls}-button-wrapper-checked`);
                if (checkedRadio) {
                    // 设置滑块宽度和偏移量
                    setSliderWidth(checkedRadio.getBoundingClientRect().width);
                    setSliderOffset(checkedRadio.offsetLeft);
                }
            });
        }
    }, [value, props.buttonStyle, props.children]); // 当value改变时重新计算位置
    const onRadioChange = (ev) => {
        const lastValue = value;
        const val = ev.target.value;
        if (!('value' in props)) {
            setValue(val);
        }
        const { onChange } = props;
        if (onChange && val !== lastValue) {
            onChange(ev);
        }
    };
    const renderGroup = () => {
        const { prefixCls: customizePrefixCls, className = '', options, optionType, buttonStyle = 'outline', disabled, children, size: customizeSize, style, id, onMouseEnter, onMouseLeave, } = props;
        const prefixCls = getPrefixCls('radio', customizePrefixCls);
        const groupPrefixCls = `${prefixCls}-group`;
        let childrenToRender = children;
        // 如果存在 options, 优先使用
        if (options && options.length > 0) {
            const optionsPrefixCls = optionType === 'button' ? `${prefixCls}-button` : prefixCls;
            childrenToRender = options.map(option => {
                if (typeof option === 'string') {
                    // 此处类型自动推导为 string
                    return (<Radio key={option} prefixCls={optionsPrefixCls} disabled={disabled} value={option} checked={value === option}>
              {option}
            </Radio>);
                }
                // 此处类型自动推导为 { label: string value: string }
                return (<Radio key={`radio-group-value-options-${option.value}`} prefixCls={optionsPrefixCls} disabled={option.disabled || disabled} value={option.value} checked={value === option.value} style={option.style}>
            {option.label}
          </Radio>);
            });
        }
        const mergedSize = customizeSize || size;
        const classString = classNames(groupPrefixCls, `${groupPrefixCls}-${buttonStyle}`, {
            [`${groupPrefixCls}-${mergedSize}`]: mergedSize,
            [`${groupPrefixCls}-rtl`]: direction === 'rtl',
        }, className);
        return (<div {...getDataOrAriaProps(props)} className={classString} style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} id={id} ref={node => {
                // 同时设置内部 ref 和外部传入的 ref
                groupRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                }
                else if (ref) {
                    ref.current = node;
                }
            }}>
        <div className={classNames(`${groupPrefixCls}-${buttonStyle}-slider`)} style={{
                display: buttonStyle === 'diffusion' && !isNil(value) ? 'block' : 'none',
                width: sliderWidth,
                left: sliderOffset,
            }}/>
        {childrenToRender}
      </div>);
    };
    return (<RadioGroupContextProvider value={{
            onChange: onRadioChange,
            value,
            disabled: props.disabled,
            name: props.name,
        }}>
      {renderGroup()}
    </RadioGroupContextProvider>);
});
export default React.memo(RadioGroup);
